import { getProductLifeCycleThunk } from '@redux/features/product-lifecycle.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import FilterInput from 'Shared/FilterInput';

const ProductLifeCycle: FC = () => {
    // -----------------------------------------------------------------
    const [page, setPage] = useState<number>(1);
    const [eventType, setEventType] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    // -----------------------------------------------------------------

    const dispatch = useAppDispatch();
    const { productLifeCycle, loading } = useAppSelector((state: RootState) => state.productLifeCycleState);
    // -----------------------------------------------------------------

    const changePage = (p: string) => setPage(Number(p));
    // -----------------------------------------------------------------

    // Event type options
    const eventTypeOptions = [
        { value: '', label: 'All Event Types' },
        { value: 'RECEIVED_IN_WAREHOUSE', label: 'Received In Warehouse' },
        { value: 'MOVED_WITHIN_WAREHOUSE', label: 'Moved Within Warehouse' },
        { value: 'PICKED_FOR_ORDER', label: 'Picked For Order' },
        { value: 'PACKED_FOR_SHIPMENT', label: 'Packed For Shipment' },
        { value: 'SHIPPED_OUT', label: 'Shipped Out' },
        { value: 'SOLD_TO_CUSTOMER', label: 'Sold To Customer' },
        { value: 'RETURNED_TO_WAREHOUSE', label: 'Returned To Warehouse' },
        { value: 'DAMAGED', label: 'Damaged' },
        { value: 'EXPIRED', label: 'Expired' },
        { value: 'DECOMMISSIONED', label: 'Decommissioned' },
        { value: 'ADDED_TO_CART', label: 'Added To Cart' },
        { value: 'REMOVED_FROM_CART', label: 'Removed From Cart' },
        { value: 'IN_TOTE', label: 'In Tote' },
        { value: 'AVAILABLE', label: 'Available' },
        { value: 'RETURNS', label: 'Returns' },
        { value: 'WAIT_TO_RETURNS', label: 'Wait To Returns' },
    ];

    // Helper function to format date to YYYY-MM-DDTHH:MM:SS format
    const formatDateForAPI = (date: Date, isEndDate: boolean = false): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        // Start date: 00:00:00, End date: 23:59:59 to cover entire day
        const time = isEndDate ? '23:59:59' : '00:00:00';
        return `${year}-${month}-${day}T${time}`;
    };

    // Handle date range changes - only send when both dates are selected
    const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
        setDateRange(dates);
        const [start, end] = dates;
        // Only update string dates when both start and end are selected
        if (start && end) {
            setStartDate(formatDateForAPI(start, false));
            setEndDate(formatDateForAPI(end, true));
        } else if (!start && !end) {
            // Clear both when range is cleared
            setStartDate('');
            setEndDate('');
        }
        // If only start is selected, don't update the string dates yet
    };

    const handleEventTypeChange = (value: string) => {
        setEventType(value);
    };

    // -----------------------------------------------------------------

    useEffect(() => {
        setPage(1);
    }, [eventType, startDate, endDate]);

    useEffect(() => {
        dispatch(
            getProductLifeCycleThunk({
                size: 20,
                page: page - 1,
                eventType: eventType || '',
                startDate: startDate || '',
                endDate: endDate || '',
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, eventType, startDate, endDate]);
    // -----------------------------------------------------------------

    // Helper function to format date for display
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // Helper function to format event type for display
    const formatEventType = (eventType: string | null | undefined) => {
        if (!eventType) return '-';
        return eventType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // -----------------------------------------------------------------

    return (
        <>
            <BreadCrumb pageTitle={`Product Life Cycle`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Product Life Cycle List</h3>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-5 flex-wrap">
                            {/* Event Type Filter */}
                            <div className="flex-1 min-w-[200px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Event Type</label>
                                <FilterInput
                                    value={eventType}
                                    onChange={handleEventTypeChange}
                                    options={eventTypeOptions}
                                    id="eventType"
                                    placeholder="All Event Types"
                                    optionLabelKey="label"
                                    optionValueKey="value"
                                />
                            </div>
                            {/* Start Date - End Date Range Picker */}
                            <div className="flex-1 min-w-[300px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Date Range</label>
                                <DatePicker
                                    selected={dateRange[0]}
                                    onChange={handleDateRangeChange}
                                    startDate={dateRange[0]}
                                    endDate={dateRange[1]}
                                    selectsRange
                                    isClearable
                                    placeholderText="Select date range"
                                    className="form-input border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                                    dateFormat="yyyy-MM-dd"
                                />
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Event Type</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Quantity</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Product ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Product Batch ID
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Performed By</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Event Timestamp
                                                </th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={(productLifeCycle as any)?.content?.length ?? 0} animationKey={`plc-${page}`}>
                                            {(productLifeCycle as any)?.content?.map((item: any) => (
                                                <AnimatedTableRow
                                                    rowKey={item?.id}
                                                    detailRecord={item}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{item?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {formatEventType(item?.eventType)}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{item?.quantity ?? `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.productId || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.productBatchId || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.performedBy || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {formatDate(item?.eventTimestamp)}
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={(productLifeCycle as any)?.pageable?.pageSize || 10}
                                    total={(productLifeCycle as any)?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={
                                        (productLifeCycle as any)?.numberOfElements ||
                                        (productLifeCycle as any)?.content?.length ||
                                        0
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default ProductLifeCycle;
